import { MvxFactorStates } from './MetabolicVulnerability';
import { resolveBlueprintSteps } from './mvxBlueprint';

/**
 * Systemic Formulas product mapping for Darren Schmidt, DC's 7-Step Blueprint
 * (+ Step O, Optimization). Parallels `mvxBlueprint.ts`: this holds the products
 * per step; `resolveMvxSupplements` zips them with the resolved Blueprint steps so
 * the panel can emphasize the products for steps this patient's MVX states
 * indicate and dim the rest (mirrors the Supplement Protocols panel).
 *
 * Brand = Systemic Formulas (our primary company), NOT CellCore. Product FUNCTIONS
 * are high-confidence and codes are verified; the ASSIGNMENT of products to
 * Schmidt's numbered steps is our clinical construction — not documented by
 * Schmidt or SF. No dosing. See project memory `project_mvx_7step_blueprint`.
 *
 * MVX is not a Section and has no DB categories, so this is a STATIC data module
 * (like `holobiomeKits.ts`) — not the findConsiderations / cat-500xxx path.
 */

export interface MvxSupplement {
  /** Product name as Systemic Formulas lists it. */
  name: string;
  /** SF product number(s), e.g. "#491" or "#810 caps / #811 LQ". */
  code?: string;
  /** Short qualifier, e.g. "then" (sequenced within the step) or "targeted". */
  note?: string;
  link?: string;
}

export interface MvxStepSupplements {
  /** Matches MVX_BLUEPRINT_STEPS[].num (1–7); 0 = Step O (Optimization). */
  step: number;
  products: MvxSupplement[];
}

// Systemic Formulas product page (WooCommerce): /product/<slug>/. Slugs follow
// the site's own pattern (e.g. EnerGenic → energenic, VRM3 → vrm3). The single-
// word / -Genic / VRM slugs are solid; a few multi-word ones (Terra µBiomic,
// Clear 1/2 µBiomic, Liver S, Kidney, VIVI Virox) are best-guess and worth a
// click-through to confirm. Products without a real single page (#6, the generic
// vitamins/minerals/glandulars line) carry no link and render as plain text.
const sf = (slug: string): string => `https://systemicformulas.com/product/${slug}/`;

// Verified SF product → step. The VRM1→VRM4 series (large → small → micro →
// cellular parasites) maps almost 1:1 onto steps 4 / 5 / 7.
export const MVX_STEP_SUPPLEMENTS: MvxStepSupplements[] = [
  {
    step: 1, // Foundational Foods
    products: [
      { name: 'EnzyGenic', link: sf('enzygenic') },
      { name: 'MBC', link: sf('mbc') },
      { name: 'Terra µBiomic', link: sf('terra-ubiomic') },
      { name: 'Terra Superfood', link: sf('terra-superfood') },
    ],
  },
  {
    step: 2, // Energy
    products: [{ name: 'eNRG LQ', code: '#811 LQ', link: 'https://systemicformulas.com/product/enrg-lq/' }],
  },
  {
    step: 3, // Drainage
    products: [
      { name: 'CXLs – Liver S', link: 'https://systemicformulas.com/product/cxls-liver-s/' },
      { name: 'CXK – Kidney', link: 'https://systemicformulas.com/product/cxk-kidney/' },
      { name: 'KDIR', link: sf('kdir') },
      { name: 'CXACX', link: sf('cxacx') },
    ],
  },
  {
    step: 4, // Intestines & Immune Cleansing
    products: [
      { name: 'VRM1', code: '#491', link: sf('vrm1') },
      { name: 'VRM3', link: sf('vrm3') },
      { name: 'Bactrex', code: '#3', link: sf('bactrex') },
      { name: 'Clear 1 µBiomic', link: sf('clear-1-ubiomic') },
      { name: 'Clear 2 µBiomic', link: sf('clear-2-ubiomic') },
      { name: '#6', note: 'then' },
      { name: 'DSIR', note: 'then', link: sf('dsir') },
    ],
  },
  {
    step: 5, // Full-Body Immune Cleansing
    products: [
      { name: 'VRM2', code: '#492', link: sf('vrm2') },
      { name: 'Bactrex', code: '#3', link: sf('bactrex') },
      { name: 'VIVI Virox', code: '#488', link: sf('vivi-virox') },
    ],
  },
  {
    step: 6, // Powerful Detoxification
    products: [
      { name: 'BIND', link: sf('bind') },
      { name: 'GCEL', link: sf('gcel') },
      { name: 'DetoxiGenic', link: sf('detoxigenic') },
    ],
  },
  {
    step: 7, // Cellular Immune Cleansing
    products: [
      { name: 'VRM4', link: sf('vrm4') },
      { name: 'GCEL', link: sf('gcel') },
      { name: 'BindGenic', link: sf('bindgenic') },
      { name: 'MucosaGenic', link: sf('mucosagenic') },
    ],
  },
  {
    step: 0, // Step O — Optimization (runs throughout)
    products: [
      { name: 'ROX', link: sf('rox') },
      { name: 'DV3', link: sf('dv3') },
      { name: 'Vitamins / Minerals / Glandulars', note: 'targeted' },
    ],
  },
];

export interface MvxSupplementStep {
  /** 1–7, or 0 for Step O (Optimization). */
  step: number;
  title: string;
  focus: string;
  /** True when this patient's MVX states indicate the step (Step O = always). */
  indicated: boolean;
  products: MvxSupplement[];
}

// Step O has no Blueprint trigger — Optimization runs throughout every step.
const OPTIMIZATION = {
  title: 'Optimization',
  focus: 'Minerals, vitamins and glandulars — supportive throughout every step.',
};

/**
 * Zip the SF product lists with the resolved Blueprint steps: each step carries
 * its title/focus and whether the patient's states indicate it (emphasized) or
 * not (dimmed). Step O is always indicated (runs throughout). Pure.
 */
export function resolveMvxSupplements(states: MvxFactorStates): MvxSupplementStep[] {
  const steps = resolveBlueprintSteps(states);
  return MVX_STEP_SUPPLEMENTS.map(entry => {
    if (entry.step === 0) {
      return { step: 0, title: OPTIMIZATION.title, focus: OPTIMIZATION.focus, indicated: true, products: entry.products };
    }
    const bp = steps.find(s => s.num === entry.step);
    return {
      step: entry.step,
      title: bp ? bp.title : `Step ${entry.step}`,
      focus: bp ? bp.focus : '',
      indicated: bp ? bp.indicated : false,
      products: entry.products,
    };
  });
}
